





























































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import BrandMixin from "@/mixins/brandMixin";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { PaymentPeriodsMap } from "@/data/product";
import { ConfirmModalModes } from "@/data/enums";
import { Inheritance } from "@/data/enums/priceLists";
import { ProductTypes } from "@/data/enums/products";

// Types
import { IState } from "@/store";
import {
  IProduct,
  IProductPriceRecalculation,
  IPricelist,
  IProductPricesIssueMessages,
  IProductPricesIssueMessage,
  IProductPrice,
  IProductPriceMatrixShort
} from "@/models/products";
import { ICurrency } from "@/models/constants";
import { ITldForm, ITldFormPrice } from "@/modules/web_hosting/models/domains";

@Component({
  components: {
    "product-price-matrix": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/productPriceMatrix.vue"
      ),
    "price-issue-messages": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/priceIssueMessages.vue"
      )
  },
  computed: {
    ...mapState({
      priceLists(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PRICELISTS,
          scope: this.priceListsScope
        });
      },
      priceListsScope(state: IState, getters) {
        return getters[`data/${DataModules.PRICELISTS}/priceListsScope`](
          this.brandId
        );
      },
      productWarningMessages(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/warningMessages`](
          this.productId
        );
      }
    })
  }
})
export default class TldPricing extends Mixins(
  RequestPromises,
  HasValidation,
  CurrenciesStore,
  BillingCyclesStore,
  BrandMixin
) {
  // Refs

  public declare $refs: {
    productPricesMatrix: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Getters

  public priceLists!: IPricelist[];
  public priceListsScope!: string;
  public productWarningMessages!: IProductPricesIssueMessages[];

  // Props

  @Prop({ required: true })
  public value!: ITldForm["prices"];

  @Prop({ default: null })
  public tld!: IProduct;

  @Prop({ required: true })
  public isDisabled!: boolean;

  // Data

  public isLoading = false;
  public isReloading = false;
  public requestError = false;

  public recurringProduct = true;
  public recalculation = {} as IProductPriceRecalculation;
  public paymentPeriods = PaymentPeriodsMap();

  public activePriceListId: IPricelist["id"] = "";
  public usedCurrencies: ICurrency[] = [];
  public savedUsedTerms: number[] = [];
  public priceErrors: any = {};
  public productPricesMatrixUUID = "";
  public changedTerms: {
    oldTerm: number;
    newTerm: number;
  }[] = [];
  public useSelectedProductPrices = false;
  public selectedProductPrices: IProductPriceMatrixShort[] = [];
  public isProcessing = false;

  // Getter

  public get prices() {
    return this.value;
  }

  // Setter

  public set prices(value) {
    this.$emit("input", value);
  }

  // Life cycle methods

  public async created() {
    if (!this.isContentAvailable) return;

    this.isLoading = true;
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PRICELISTS,
      scope: this.priceListsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getPriceLists(),
        this.getCurrencies(),
        this.getBillingCycles()
      ]);

      this.activePriceListId = this.brandPriceListId;
      this.initiateMatrixMemory();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getPriceLists() {
    return this.$store.dispatch(`data/${DataModules.PRICELISTS}/list`, {
      scope: this.priceListsScope,
      vm: this,
      params: { limit: 0, with: "parents" }
    });
  }

  public initiateMatrixMemory() {
    this.usedCurrencies = _.uniqBy(
      _.compact(
        _.concat(
          [this.brandCurrency],
          this.productPrices.length
            ? _.map(
                _.uniqBy(this.productPrices, "currency_id"),
                price =>
                  _.find(this.currencies, [
                    "id",
                    price["currency_id"]
                  ]) as ICurrency
              )
            : this.brandCurrencies
        )
      ),
      "id"
    );

    this.savedUsedTerms = this.productPrices.length
      ? _.map(
          _.uniqBy(this.productPrices, "billing_cycle_months"),
          price => price["billing_cycle_months"]
        )
      : [12];

    this.initializePriceErrors();
    this.changedTerms = [];
    this.productPricesMatrixUUID = this.$uuid();
  }

  public initializePriceErrors() {
    _.forEach(this.priceLists, priceList => {
      const pricelistErrors = {};

      _.forEach(this.usedTerms, usedTerm => {
        _.forEach(this.usedCurrencies, (currency: ICurrency) => {
          if (
            currency.id === this.brandCurrencyId &&
            _.some(
              this.productPrices,
              productPrice =>
                productPrice.pricelist_id === priceList.id &&
                productPrice.billing_cycle_months === usedTerm
            ) &&
            !_.some(this.productPrices, (productPrice: IProductPrice) => {
              return (
                productPrice.pricelist_id === priceList.id &&
                productPrice.billing_cycle_months === usedTerm &&
                productPrice.currency_id === currency.id &&
                productPrice.price !== null
              );
            })
          ) {
            pricelistErrors[`${currency.code}_${usedTerm}`] = [
              this.$t("_sentence.amount_field_required")
            ];
          }
        });
      });

      this.$_.forEach(
        _.filter(this.$_.get(this.productWarningMessages, "data", []), [
          "pricelist_id",
          priceList.id
        ]),
        error => {
          const code = this.getCurrencyCode(error.currency_id);
          const term = error.billing_cycle_months;
          const errorKey = `${code}_${term}`;
          const errorMessage = this.$_.get(
            this.productWarningMessages,
            "message",
            ""
          );

          if (_.get(pricelistErrors, errorKey, []).length) {
            pricelistErrors[errorKey].push(errorMessage);
          } else {
            pricelistErrors[errorKey] = [errorMessage];
          }
        }
      );

      this.$set(this.priceErrors, priceList.id, pricelistErrors);
    });
  }

  public initiateFormPrices() {
    this.savedUsedTerms = this.usedTerms;
  }

  public async addCurrency() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/openSelectProductCurrencyModal`,
      {
        props: {
          currencies: this.currencies,
          usedCurrenciesIds: _.map(
            this.usedCurrencies,
            (usedCurrency: ICurrency) => usedCurrency.id
          ),
          isMultiple: true
        },
        events: {
          success: results => {
            this.addNewCurrencies(results);
          }
        }
      }
    );
  }

  public async addNewCurrencies(currenciesIds) {
    _.forEach(currenciesIds, currencyId => {
      this.usedCurrencies.push(
        _.find(this.currencies, ["id", currencyId]) as ICurrency
      );
    });

    setTimeout(() => {
      _.forEach(this.priceLists, priceList => {
        const usedPrices = this.getPriceListPrices(
          priceList.id
        ) as IProductPrice[];

        if (
          !usedPrices.length ||
          priceList.inheritance === Inheritance.INHERITED
        )
          return;

        _.forEach(this.usedTerms, usedTerm => {
          _.forEach(this.usedCurrencies, (currency: ICurrency) => {
            if (currency.id !== this.brandCurrencyId) return;

            const currencyCode = this.getCurrencyCode(currency.id);
            const usedTermErrors = _.get(
              this.priceErrors[priceList.id],
              `${currencyCode}_${usedTerm}`,
              []
            );
            if (
              _.some(
                usedPrices,
                productPrice =>
                  productPrice.pricelist_id === priceList.id &&
                  productPrice.billing_cycle_months === usedTerm
              ) &&
              !_.some(usedPrices, (productPrice: IProductPrice) => {
                return (
                  productPrice.pricelist_id === priceList.id &&
                  productPrice.billing_cycle_months === usedTerm &&
                  productPrice.currency_id === this.brandCurrencyId &&
                  productPrice.price !== null
                );
              })
            ) {
              if (
                usedTermErrors.length &&
                _.some(
                  usedTermErrors,
                  message => message.indexOf("is required") !== -1
                )
              )
                return;

              usedTermErrors.push(this.$t("_sentence.amount_field_required"));

              this.$set(
                this.priceErrors[priceList.id],
                `${currencyCode}_${usedTerm}`,
                usedTermErrors
              );
            }
          });
        });
      });
    }, 50);
  }

  public showRemoveCurrencyConfirmation(currencyId: ICurrency["id"]) {
    return _.some(this.productPrices, priceObj => {
      return (
        priceObj.currency_id === currencyId &&
        priceObj.price !== null &&
        priceObj.own_price &&
        priceObj.pricelist_id !== this.brandPriceListId
      );
    });
  }

  public async removeCurrency(currencyId: ICurrency["id"]) {
    if (!this.showRemoveCurrencyConfirmation(currencyId)) {
      this.filterOutCurrency(currencyId);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_currency_not_empty")
        },
        events: {
          confirmed: async () => {
            this.filterOutCurrency(currencyId);
          }
        }
      }
    });
  }

  public filterOutCurrency(currencyId: ICurrency["id"]) {
    this.usedCurrencies = _.filter(
      this.usedCurrencies,
      currency => currency.id !== currencyId
    );

    setTimeout(() => {
      _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
        const usedPrices = this.getPriceListPrices(
          errorPricelistId
        ) as IProductPrice[];

        _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
          const [code, term] = priceErrorKey.split("_");

          if (
            code === this.brandCurrencyCode &&
            !_.some(
              usedPrices,
              usedPrice =>
                usedPrice.billing_cycle_months === _.toNumber(term) &&
                code !== this.brandCurrencyCode
            ) &&
            _.some(
              priceErrorValue,
              message => message.indexOf("is required") !== -1
            )
          ) {
            this.$set(
              this.priceErrors[errorPricelistId],
              priceErrorKey,
              _.filter(
                priceErrorValue,
                message => message.indexOf("is required") === -1
              )
            );
          }
        });
      });
    }, 50); // To avoid racing conditions
  }

  public addTerm(termId: number) {
    this.savedUsedTerms.push(termId);
  }

  public removeTerm(term: number) {
    if (!this.showRemoveTermConfirmation(term)) {
      this.filterOutTerm(term);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_term_not_empty")
        },
        events: {
          confirmed: () => {
            this.filterOutTerm(term);
          }
        }
      }
    });
  }

  public filterOutTerm(term: number) {
    this.savedUsedTerms = _.filter(
      this.savedUsedTerms,
      savedUsedTerm => savedUsedTerm !== term
    );
  }

  public updateTerm(oldTerm, newTerm) {
    if (
      !this.showRemoveTermConfirmation(oldTerm) ||
      _.find(
        this.changedTerms,
        changedTerm =>
          changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm
      )
    ) {
      this.changeTerm(oldTerm, newTerm);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          title: this.$t("_.change_term"),
          confirmButtonText: this.$t("_action.change"),
          message: this.$t("_sentence.change_term_not_empty")
        },
        events: {
          confirmed: () => {
            this.changeTerm(oldTerm, newTerm);
          }
        }
      }
    });
  }

  public changeTerm(oldTerm: number, newTerm: number) {
    if (
      _.find(
        this.changedTerms,
        changedTerm =>
          changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm
      )
    ) {
      this.changedTerms = _.filter(
        this.changedTerms,
        changedTerm =>
          !(changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm)
      );
    }

    this.$refs.productPricesMatrix.changeTermPrices(oldTerm, newTerm);

    _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
      _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
        const [code, term] = priceErrorKey.split("_");

        if (term === oldTerm.toString()) {
          delete this.priceErrors[errorPricelistId][priceErrorKey];
          this.$set(
            this.priceErrors[errorPricelistId],
            `${code}_${newTerm}`,
            priceErrorValue
          );
        }
      });
    });

    this.changedTerms.push({ oldTerm, newTerm });

    this.filterOutTerm(oldTerm);
    this.savedUsedTerms.push(newTerm);
  }

  public showRemoveTermConfirmation(term: number) {
    return _.some(this.productPrices, priceObj => {
      return (
        priceObj.billing_cycle_months === term &&
        priceObj.price !== null &&
        priceObj.own_price &&
        priceObj.pricelist_id !== this.brandPriceListId
      );
    });
  }

  public getPriceListPrices(priceListId) {
    if (this.activePriceListId !== priceListId) {
      const oldTerms = _.map(
        this.changedTerms,
        changedTerm => changedTerm.oldTerm
      );

      let priceListPrices = _.map(
        _.filter(this.productPrices, priceObj => {
          return (
            priceObj.own_price &&
            priceObj.pricelist_id === priceListId &&
            this.usedCurrenciesIds.includes(priceObj.currency_id) &&
            (this.usedTerms.includes(priceObj.billing_cycle_months) ||
              oldTerms.includes(priceObj.billing_cycle_months))
          );
        }),
        price =>
          _.pick(price, [
            "currency_id",
            "billing_cycle_months",
            "price",
            "pricelist_id"
          ])
      );

      if (this.changedTerms.length) {
        _.forEach(this.changedTerms, changedTerm => {
          priceListPrices = _.map(priceListPrices, priceListPrice => {
            if (priceListPrice.billing_cycle_months === changedTerm.oldTerm) {
              priceListPrice.billing_cycle_months = changedTerm.newTerm;
            }
            return priceListPrice;
          });
        });
      }

      return priceListPrices;
    }

    return this.prices;
  }

  public addIssue(
    data: IProductPricesIssueMessages[],
    issueMessage: IProductPricesIssueMessage,
    priceListId: IPricelist["id"]
  ) {
    const index = _.findIndex(data, ["priceListId", priceListId]);

    if (index > -1) {
      if (issueMessage.isError) {
        const errorIndex = _.findIndex(
          data[index].errors,
          currentError =>
            currentError.billing_cycle_months ===
              issueMessage.billing_cycle_months &&
            currentError.currency_id === issueMessage.currency_id
        );
        if (errorIndex > -1) {
          data[index].errors[errorIndex] = issueMessage;
        } else {
          data[index].errors.push(issueMessage);
        }
        data[index].warnings = _.filter(
          data[index].warnings,
          (warning: IProductPricesIssueMessage) => {
            return !(
              warning.billing_cycle_months ===
                issueMessage.billing_cycle_months &&
              warning.currency_id === issueMessage.currency_id
            );
          }
        );
      } else {
        data[index].warnings.push(issueMessage);
      }
    } else {
      data.push({
        priceListId: priceListId,
        warnings: issueMessage.isError ? [] : [issueMessage],
        errors: issueMessage.isError ? [issueMessage] : []
      });
    }

    return data;
  }

  public autoPopulate() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openAutoPopulateModal`,
      {
        props: {
          prices: this.prices,
          activePriceListId: this.activePriceListId,
          usedCurrencies: this.usedCurrencies,
          useYearsOnly: true
        },
        events: {
          success: resultForm => {
            this.usedCurrencies = _.uniqBy(
              _.concat(
                this.usedCurrencies,
                _.map(
                  _.uniqBy(resultForm.prices, "currency_id"),
                  price =>
                    _.find(this.currencies, [
                      "id",
                      price["currency_id"]
                    ]) as ICurrency
                )
              ),
              "id"
            );

            this.savedUsedTerms = _.uniq(
              _.concat(this.savedUsedTerms, resultForm.selectedTerms)
            );

            _.forEach(resultForm.prices, newPrice => {
              _.forEach(resultForm.selectedTerms, (selectedTerm: number) => {
                const priceIndex = _.findIndex(
                  this.prices,
                  formPrice =>
                    formPrice.pricelist_id === this.activePriceListId &&
                    formPrice.billing_cycle_months === selectedTerm &&
                    formPrice.currency_id === newPrice.currency_id
                );
                const calculatedMonthPrice =
                  resultForm.usedTerm === 1
                    ? selectedTerm * newPrice.price
                    : selectedTerm < 12
                    ? Math.round(
                        (selectedTerm * (newPrice.price / 12) +
                          Number.EPSILON) *
                          100
                      ) / 100
                    : (selectedTerm / 12) * newPrice.price;
                if (priceIndex !== -1) {
                  if (!resultForm.overwrite) return;
                  this.prices[priceIndex].price = calculatedMonthPrice;
                } else {
                  this.prices.push({
                    ..._.pick(newPrice, ["pricelist_id", "currency_id"]),
                    billing_cycle_months: selectedTerm,
                    price: calculatedMonthPrice
                  });
                }
              });
              const priceIndex = _.findIndex(
                this.prices,
                formPrice =>
                  formPrice.pricelist_id === this.activePriceListId &&
                  formPrice.billing_cycle_months ===
                    newPrice.billing_cycle_months &&
                  formPrice.currency_id === newPrice.currency_id
              );
              if (priceIndex !== -1) {
                if (!resultForm.overwrite) return;
                this.prices[priceIndex].price = newPrice.price;
              } else {
                this.prices.push(newPrice as ITldFormPrice);
              }
            });
            this.selectedProductPrices = _.map(
              this.prices,
              (price: ITldFormPrice) => {
                return {
                  ...price,
                  currency_code: _.get(
                    _.find(this.currencies, ["id", price.currency_id]),
                    "code"
                  ),
                  own_price: true
                };
              }
            ) as IProductPriceMatrixShort[];
            this.useSelectedProductPrices = true;
            this.productPricesMatrixUUID = this.$uuid();
          }
        }
      }
    );
  }

  public autoPopulateFromProduct() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        props: {
          productTypes: [
            ProductTypes.SINGLE_PRODUCT,
            ProductTypes.PRODUCT_BUNDLE
          ],
          selectedProducts: [],
          disabledProducts: [],
          isMultiselect: false
        },
        events: {
          success: async results => {
            const selectedProductId = _.get(results, "[0].id");

            try {
              this.isProcessing = true;

              const selectedProduct = await this.$store.dispatch(
                `data/${DataModules.CATALOGUE}/get`,
                {
                  id: selectedProductId,
                  returnData: true,
                  storeData: false,
                  vm: this
                }
              );
              this.selectedProductPrices = _.get(selectedProduct, "prices", []);
              this.$set(
                this,
                "recurringProduct",
                _.get(selectedProduct, "billing_cycle_months", 0) > 0
              );
              this.useSelectedProductPrices = true;
              this.initiateMatrixMemory();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
            }
          }
        }
      }
    );
  }

  // Computed

  public get parentPriceListName() {
    return _.get(
      _.find(this.priceLists, ["id", this.activePriceListId]),
      "parents[0]name",
      ""
    );
  }

  public get productPrices() {
    if (this.useSelectedProductPrices) {
      return this.selectedProductPrices;
    }

    return _.get(this.tld, "prices", []) || [];
  }

  public get activePricelist() {
    return _.find(this.priceLists, ["id", this.activePriceListId]);
  }

  public get productPricesMatrixKey() {
    return `${this.activePriceListId}_${this.productPricesMatrixUUID}`;
  }

  public get multicurrencyOptions() {
    return [
      {
        leftLabel: this.$t("_.omit_product") as string,
        desc: this.$t("_sentence.omit_product_desc") as string,
        value: 0
      },
      {
        leftLabel: this.$t("_.make_available") as string,
        desc: this.$t("_sentence.make_available_desc", {
          currency: this.brandCurrencyCode
        }) as string,
        value: 1
      },
      {
        leftLabel: this.$t("_.inherit_brand_default_value", {
          value: this.$t("_.omit_product")
        }) as string,
        value: 2
      }
    ];
  }

  public get usedCurrenciesIds() {
    return _.sortBy(
      _.map(this.usedCurrencies, (usedCurrency: ICurrency) => usedCurrency.id)
    );
  }

  public get priceListsNeedsUpdate() {
    if (!this.productPrices.length) {
      return [this.activePriceListId];
    }

    const originalCurrencies = _.sortBy(
      _.map(
        _.uniqBy(this.productPrices, "currency_id"),
        productPrice => productPrice.currency_id
      )
    );

    const originalTerms = _.sortBy(
      _.map(
        _.uniqBy(this.productPrices, "billing_cycle_months"),
        price => price["billing_cycle_months"]
      )
    );

    return _.uniq(
      _.concat(
        [this.activePriceListId],
        _.map(
          _.uniqBy(
            _.filter(this.productPrices, productPrice => {
              return (
                productPrice.pricelist_id !== this.activePriceListId &&
                productPrice.own_price &&
                (_.difference(
                  originalCurrencies,
                  this.usedCurrenciesIds
                ).includes(productPrice.currency_id) ||
                  _.difference(originalTerms, this.usedTerms).includes(
                    productPrice.billing_cycle_months
                  ))
              );
            }),
            "pricelist_id"
          ),
          price => price.pricelist_id
        )
      )
    );
  }

  public get hasPriceValidationErrors() {
    return _.some(
      _.filter(
        this.priceErrors[this.activePriceListId],
        (priceErrorValue, priceErrorKey) => {
          const [code, term] = priceErrorKey.split("_");
          return (
            this.usedTerms.includes(_.toNumber(term)) &&
            _.map(this.usedCurrencies, currency => currency["code"]).includes(
              code
            )
          );
        }
      ),
      errorValue => {
        return !!errorValue.length;
      }
    );
  }

  public get usedTerms() {
    return _.filter(this.savedUsedTerms, term => term > 0);
  }

  public get issueMessages() {
    let data: IProductPricesIssueMessages[] = [];

    // Fill all missing price warnings
    _.forEach(this.usedTerms, usedTerm => {
      _.forEach(this.usedCurrencies, (currency: ICurrency) => {
        if (
          currency.id !== this.brandCurrencyId &&
          !_.some(
            this.activePriceListId === this.brandPriceListId
              ? this.prices
              : this.productPrices,
            (price: IProductPrice) => {
              return (
                price.pricelist_id === this.brandPriceListId &&
                price.billing_cycle_months === usedTerm &&
                price.currency_id === currency.id &&
                price.price !== null
              );
            }
          )
        ) {
          data = this.addIssue(
            data,
            {
              billing_cycle_months: usedTerm,
              currency_id: currency.id,
              message: this.$t("_sentence.missing_prices_msg").toString(),
              isError: false
            },
            this.brandPriceListId
          );
        }
      });
    });

    // Fill validation errors (FE and 422s)
    _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
      _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
        const [code, term] = priceErrorKey.split("_");

        if (
          this.usedTerms.includes(_.toNumber(term)) &&
          _.map(this.usedCurrencies, currency => currency["code"]).includes(
            code
          ) &&
          priceErrorValue.length
        ) {
          data = this.addIssue(
            data,
            {
              billing_cycle_months: _.toNumber(term),
              currency_id: _.get(
                _.find(this.currencies, ["code", code]),
                "id",
                ""
              ),
              message: priceErrorValue[0],
              isError: true
            },
            errorPricelistId
          );
        }
      });
    });

    return _.sortBy(data, "priceListId");
  }

  // Watchers

  @Watch("hasPriceValidationErrors")
  public onPriceErrorsChange(value) {
    this.$emit("hasPriceErrors", value);
  }

  // Guarding

  public guardFunctionalities = ["staff_get_product"];

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
